import SanitizeHtml from 'sanitize-html'
import ShortUniqueId, { ShortUniqueIdOptions } from 'short-unique-id'
import { stringSimilarity } from 'string-similarity-js'
import { isObject, natsortArray, isEqualObjects, isEqualArrays } from '@utils/objects'
import { SlugOptions, slugify } from '@utils/slug'
import { isDate } from 'date-fns'
import { compareDate } from '@utils/dates'

export function uniqueId(options: Partial<ShortUniqueIdOptions> = { length: 10 }): string {
  const { randomUUID } = new ShortUniqueId(options);
  return randomUUID();
}

export function createPageIdentifier(identifier: string, path: string = '/'): string {
  identifier = trim(identifier, '/');
  path = trim(path, '/');

  return `${identifier}-${(path ? path : 'index').replace('/', '-')}`;
}

export function createEmailIdentifier(email: string): string {
  email = trim(email);
  return email.replace(/\W/gi, '_');
}

export function parseStringToNumber(string: string): number | null {
  const number = Number(string);

  if (isNaN(number) || number.toString() !== string) {
    return null;
  }

  return number;
}

/**
 * Turn a number (maybe float with decimal dot) to a decimal comma.
 * Ignore if not number.
 *
 * @param number
 * @returns
 */
export function maybeNumberWithCommaDecimal(number: any): any {
  if (typeof number === 'number') {
    number = String(number).replace(',', '').replace('.', ',');
  }

  return number;
}

export function countDecimals(number: number | string): number {
  const parts = String(number).split('.');
  return parts.length === 2 ? parts[1].length : 0;
}

export function isTextSimilar(text: string | string[], challenge: string | string[], similarity: number = 0.9): boolean {
  if (! Array.isArray(text)) {
    text = [text];
  }

  if (! Array.isArray(challenge)) {
    challenge = [challenge];
  }

  for (let i = 0; i < text.length; i++) {
    for (let j = 0; j < challenge.length; j++) {
      if (textSimilarity(text[i], challenge[j]) >= similarity) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Normalizes a string by removing accents, optionally converting to lowercase,
 * and optionally generating a slug.
 *
 * - Removes diacritics using Unicode normalization (NFD).
 * - Converts the text to lowercase if `toLower` is true.
 * - Generates a slug if `slug` is true or an object with slug options (`SlugOptions`).
 *
 * @param text
 * @param toLower
 * @param slug
 * @returns
 */
export function normalize(text: string, toLower: boolean = false, slug: boolean | SlugOptions = false): string {
  text = text.toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  text = toLower ? text.toLowerCase() : text;
  text = trim(text);

  if (slug === false) {
    return text;
  }

  slug = (isObject(slug) ? slug : {}) as SlugOptions;
  slug.lower = toLower;

  return slugify(text, slug);
}

/**
 * Check text similarity
 *
 * @param text
 * @param challenge
 * @param shouldNormalize
 * @returns
 */
export function textSimilarity(text: string, challenge: string, shouldNormalize: boolean = true): number {
  text = text.trim();
  challenge = challenge.trim();

  if (shouldNormalize) {
    text = normalize(text);
    challenge = normalize(challenge);
  }

  const length = text.length <= 5 ? 1 : 2;
  return stringSimilarity(text, challenge, length);
}

/**
 * Trim a string
 *
 * @param string
 * @param chars
 * @returns
 */
export function trim(string: string, chars: string = ' '): string {
  const parts = chars.split('');

  let start = 0;
  let end = string.length;

  while (start < end && parts.indexOf(string[start]) >= 0) {
    ++start;
  }

  while (end > start && parts.indexOf(string[end - 1]) >= 0) {
    --end;
  }

  return start > 0 || end < string.length ? string.substring(start, end) : string;
}

/**
 * Sanitize HTML tags to safe HTML content
 *
 * @param text
 * @param options
 * @returns
 */
export function sanitizeHtml(text: string, options?: SanitizeHtml.IOptions): string {
  return SanitizeHtml(text, options);
}

/**
 * Remove all HTML tags
 *
 * @param text
 * @returns
 */
export function stripHtml(text: string): string {
  return sanitizeHtml(text, { allowedTags: [] });
}

/**
 * Create paragraphs (<p></p>) for each break lines
 *
 * @param text
 * @returns
 */
export function autoParagraphs(text: string): string {
  return `<p>${ trim(text).split('\n').join('</p><p>') }</p>`;
}

/**
 * Create a humman readable text list.
 *
 * @param list
 * @param options
 * @param options.sort
 * @param options.comma
 * @param options.and
 * @param options.limit
 * @param options.ellipsis
 * @returns
 */
type TextListOptions = { sort?: boolean, comma?: string, and?: string, limit?: number, ellipsis?: string };
export function textList(list: string[], { sort = false, comma = ', ', and = ' e ', limit = 0, ellipsis = '...' }: TextListOptions = {}): string {
  list = list.filter(x => x);

  if (! list.length) {
    return '';
  }

  if (list.length === 1) {
    return list[0];
  }

  if (sort) {
    list = natsortArray(list);
  }

  const limited = limit > 0 ? list.slice(0, limit) : [...list];

  if (list.length > limited.length) {
    return limited.join( comma ) + ellipsis;
  }

  const last = limited.pop();
  return `${ limited.join( comma ) }${ and }${ last }`;
}

/**
 * Alias to textList with "ou".
 *
 * @param list
 * @param options
 * @returns
 */
export function orTextList(list: string[], options?: Omit<TextListOptions, 'and'>): string {
  return textList(list, { ...options, and: ' ou ' });
}

/**
 * Alias to textList with "<br>" on separators.
 *
 * @param list
 * @param options
 * @returns
 */
export function textListWithBreaks(list: string[], options?: Omit<TextListOptions, 'and' | 'comma'>): string {
  return textList(list, { ...options, comma: '<br>', and: '<br>' });
}

/**
 * Split a string in "-", "_", spaces and dots (or not).
 *
 * @param string
 * @param dots
 * @returns
 */
export function split(string: string, dots: boolean = true): string[] {
  const regex = dots ? /[-_\s/.]+/ : /[-_\s]+/;
  return string.toString().split(regex);
}

/**
 * Replace placeholders in a string from a key-value object.
 *
 * @param string
 * @param object
 * @param placeholderStart
 * @param placeholderEnd
 * @returns
 */
export function placeholder(string: string, object: Record<string, string>, placeholderStart: string = '[', placeholderEnd: string = ']'): string {
  for (const key in object) {
    string = string.replace(`${placeholderStart}${key}${placeholderEnd}`, object[key]);
  }

  return string;
}

/**
 * Parse a boolean string to a boolean value.
 *
 * @param string
 * @param acceptedFalseValues
 * @returns
 */
export function parseBooleanString(string: string, acceptedFalseValues: any[] = ['0', 0, null, '']): boolean {
  if (typeof string === 'undefined') {
    return false;
  }

  if (acceptedFalseValues.includes(string)) {
    return false;
  }

  return Boolean(string);
}

/**
 * Run a callback every interval but starts to count after finished.
 *
 * @param callback
 * @param interval
 * @param onStart
 * @returns
 */
export function runCallbackIntoIntervall(callback: () => void | Promise<void>, interval: number, onStart: boolean = true): () => void {
  let timeout: NodeJS.Timeout;

  const startTimeout = async (when: number = 0) => {
    if (timeout) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(async () => {
      await callback();
      startTimeout(interval);
    }, when);
  };

  const startInterval = onStart ? 0 : interval;
  startTimeout(startInterval);

  return () => clearTimeout(timeout);
}

/**
 * Abbreviate a phrase
 *
 * @param text
 * @param size
 * @param separator
 * @returns
 */
export const abbreviate = (text: string, size: number, separator: string = ' - ') => {
  if (size < 10) {
    throw new Error('[ABBREVIATE] Size is too small.');
  }

  if (text.length <= size) {
    return text;
  }

  if (text.indexOf(separator) === -1) {
    return `${ text.substring(0, size - 1) }.`;
  }

  const parts = text.split(separator);
  const last = (parts.pop() || '');

  const impossible = '________________________';
  const outputParts = parts.join(separator).replace(separator, ` ${impossible} `).split(' ');
  const output = outputParts.map(v => v === impossible ? v : (outputParts.length === 1 ? v.toUpperCase().replace(/(?<!^)[aeiou](?!.$)/gi, '').substring(0, 3) : v.toUpperCase().substring(0, 1))).join('').replace(impossible, separator);

  const lastPartSize = size - (output.length + separator.length);
  if (lastPartSize > 0) {
    if (last.length <= lastPartSize) {
      return `${ output }${ separator }${ last }`;
    }

    return `${ output }${ separator }${ last.substring(0, lastPartSize - 1) }.`;
  }

  return `${ output.substring(0, (size - 10 - separator.length)) }.${ separator }${ last.substring(0, 9) }.`;
}

/**
 * Compares two values safely, handling different data types appropriately.
 *
 * @param value1 - First value to compare
 * @param value2 - Second value to compare
 * @param isStrict - Whether to use strict equality (===) or loose equality (==)
 * @returns true if values are equal, false otherwise
 */
export function isEqual(value1: any, value2: any, isStrict: boolean = true): boolean {
  // For dates
  if (isDate(value1)) {
    if (! isDate(value2) || ! compareDate(value1, value2)) {
      return false;
    }

    return true;
  }

  // For objects
  if (isObject(value1)) {
    if (! isObject(value2)) {
      return false;
    }

    return isEqualObjects(value1, value2, [], isStrict);
  }

  // For arrays
  if (Array.isArray(value1)) {
    return isEqualArrays(value1, value2, isStrict);
  }

  // For null
  if (value1 === null) {
    return value2 === null;
  }

  // For scalar values
  return isStrict ? (value1 === value2) : (value1 == value2);
}

